/**
 * Pure helpers shared by the Goals page, its detail panel and the dashboard
 * card. Figures come from the backend summary; these only shape them for display.
 */
import i18n from '../i18n';
import { monthLong } from './intlFormatters';

// A colour from the shared picker palette, so an unset goal and its swatch agree.
export const FALLBACK_GOAL_COLOR = '#3F8DF7';

// Account types that only ever stand on the other side of a movement: a card
// or a loan never holds goal money. Mirrors orm/account_roles.py.
const COUNTERPARTY_ACCOUNT_TYPES = ['credit', 'lend'];

export const canHoldGoal = (account) =>
  account.is_active !== false && !COUNTERPARTY_ACCOUNT_TYPES.includes(account.account_type);

export const formatAmount = (value) =>
  (value || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });

const parseYMD = (s) => {
  const [y, m, d] = String(s).slice(0, 10).split('-').map(Number);
  return { y, m: m - 1, d };
};

// "March 2027" / "Marzo 2027"
export const formatMonthYear = (dateStr) => {
  if (!dateStr) return '';
  const { y, m } = parseYMD(dateStr);
  return `${monthLong(m)} ${y}`;
};

// Whole calendar months from today until the date, never negative.
export const monthsUntil = (dateStr, from = new Date()) => {
  if (!dateStr) return 0;
  const { y, m } = parseYMD(dateStr);
  return Math.max(0, (y - from.getFullYear()) * 12 + (m - from.getMonth()));
};

/** "1 year and 6 months" — how far off a date is, in words. */
export const durationWords = (dateStr, from = new Date()) => {
  const months = monthsUntil(dateStr, from);
  const years = Math.floor(months / 12);
  const rest = months % 12;
  const yearPart = years
    ? i18n.t(years === 1 ? 'goals:duration.yearOne' : 'goals:duration.yearOther', { count: years })
    : null;
  const monthPart = rest || !years
    ? i18n.t(rest === 1 ? 'goals:duration.monthOne' : 'goals:duration.monthOther', { count: rest })
    : null;
  if (yearPart && monthPart) return i18n.t('goals:duration.and', { years: yearPart, months: monthPart });
  return yearPart || monthPart;
};

export const daysLeftInMonth = (today = new Date()) =>
  new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate() - today.getDate();

export const hexToTint = (hex, alpha = 0.16) => {
  const clean = (hex || FALLBACK_GOAL_COLOR).replace('#', '');
  const n = parseInt(clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};

// How a goal is doing, for the one-word status beside its name.
export const goalStatusKey = (summary) => {
  if (!summary) return null;
  if (summary.is_achieved) return 'achieved';
  if (summary.is_overdue) return 'overdue';
  if (summary.is_off_track) return 'behindPace';
  return 'onPace';
};

// Text colours for a goal's status, readable on every ground in both modes.
export const STATUS_COLORS = {
  achieved: 'redesign.amount.income',
  overdue: 'redesign.accent.red',
  behindPace: 'redesign.text.warn',
  onPace: 'redesign.amount.income',
};
